// src/hooks/useGeolocationMap.js
import { useEffect, useState, useRef } from "react";
import mapboxgl from "mapbox-gl";
import IconMarkerPublic from "../assets/icons/icon-marker-public.svg";

const useGeolocationMap = (
  containerRef,
  initialCoords,
  style,
  userMarkerIcon,
  showUserMarker = true,
  pins = [],                         // your pins
  disableGeolocationOverride = false // ← new flag
) => {
  // start coords at whatever you passed
  const [coords, setCoords] = useState(initialCoords);
  // if we're disabling override, we already consider “loaded”
  const [geolocationLoaded, setGeolocationLoaded] = useState(
    !!disableGeolocationOverride
  );
  const [map, setMap] = useState(null);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);

  // Prevent body scroll …
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Only run geolocation if not disabling it
  useEffect(() => {
    if (disableGeolocationOverride) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newCoords = {
            lng: position.coords.longitude,
            lat: position.coords.latitude,
            zoom: coords.zoom,
          };
          setCoords(newCoords);
          setGeolocationLoaded(true);
        },
        (error) => {
          console.error("Error getting geolocation:", error);
          setGeolocationLoaded(true);
        },
        { enableHighAccuracy: true }
      );
    } else {
      setGeolocationLoaded(true);
    }
  }, [disableGeolocationOverride]);

  // Initialize the map once container & “loaded” are true
  useEffect(() => {
    if (!containerRef.current || !geolocationLoaded) return;
    const newMap = new mapboxgl.Map({
      container: containerRef.current,
      style: style || "mapbox://styles/mapbox/streets-v12",
      center: [coords.lng, coords.lat],
      zoom: coords.zoom,
    });
    newMap.addControl(new mapboxgl.NavigationControl(), "top-left");

    if (showUserMarker) {
      const el = document.createElement("div");
      if (userMarkerIcon) {
        el.style.backgroundImage = `url(${userMarkerIcon})`;
      }
      el.style.width = "40px";
      el.style.height = "40px";
      el.style.backgroundSize = "contain";
      el.style.backgroundRepeat = "no-repeat";
      userMarkerRef.current = new mapboxgl.Marker(el)
        .setLngLat([coords.lng, coords.lat])
        .addTo(newMap);
    }

    setMap(newMap);
    return () => newMap.remove();
  }, [containerRef, geolocationLoaded]);

  // Update coords on pan/zoom
  useEffect(() => {
    if (!map) return;
    const onMoveEnd = () => {
      const center = map.getCenter();
      setCoords((prev) => ({
        lng: center.lng,
        lat: center.lat,
        zoom: prev.zoom,
      }));
    };
    map.on("moveend", onMoveEnd);
    return () => map.off("moveend", onMoveEnd);
  }, [map]);

  // Render dynamic pins …
  useEffect(() => {
    if (!map) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    pins.forEach((pin) => {
      const el = document.createElement("div");
      const icon = pin.icon || IconMarkerPublic;
      el.style.backgroundImage = `url(${icon})`;
      el.style.width = pin.markerWidth || "50px";
      el.style.height = pin.markerHeight || "50px";
      el.style.backgroundSize = "contain";
      el.style.backgroundRepeat = "no-repeat";
      const m = new mapboxgl.Marker({ element: el })
        .setLngLat([pin.lng, pin.lat])
        .addTo(map);
      if (pin.onClick) {
        el.addEventListener("click", () => pin.onClick(pin));
      }
      markersRef.current.push(m);
    });
  }, [map, pins]);

  // recenter helper
  const handleRecenter = () => {
    if (!navigator.geolocation || !map) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords: c }) => {
        map.flyTo({ center: [c.longitude, c.latitude], zoom: coords.zoom });
        setCoords((prev) => ({
          ...prev,
          lng: c.longitude,
          lat: c.latitude,
        }));
        if (showUserMarker && userMarkerRef.current) {
          userMarkerRef.current.setLngLat([c.longitude, c.latitude]);
        }
      },
      (err) => console.error(err),
      { enableHighAccuracy: true }
    );
  };

  return { map, coords, geolocationLoaded, handleRecenter, setCoords };
};

export default useGeolocationMap;
